import React, { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

import { CustomPagination } from "../../customPagination";
import { ConvertISOToDate, ConvertMetersToFeet } from "../../util";
import { useUserAuth } from "../../contexts/authContext";
import { useMap } from "../../contexts/mapContext";
import { useNavigate } from "react-router";
import { GridActionsCellItem } from "@mui/x-data-grid";

import MapPreview from "../../map/mapPreview";
import ResponsiveGrid from "../../grid/responsiveGrid";

export function Alerts() {
    const [showMap, setShowMap] = useState(false);
    const [selectedAlertVolumes, setSelectedAlertVolumes] = useState([]);
    const [alertsAreSelected, setAlertsAreSelected] = useState(false);

    const navigate = useNavigate();
    const { planningOperations, publishedOperations, alertVolumes } = useMap();
    const { getOrganizationByID, handleFailedFetch, setSnackbar } = useUserAuth();

    const columns = [
        {
            field: "name",
            headerName: "Name",
            editable: false,
            minWidth: 150,
            flex: 1
        },
        {
            field: "time_start",
            headerName: "Start",
            editable: false,
            minWidth: 200,
            type: "dateTime",
            flex: 1,
            valueFormatter: (value) => `${ConvertISOToDate(value)}`
        },
        {
            field: "time_end",
            headerName: "End",
            editable: false,
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) => (row.permanent ? "Permanent" : `${ConvertISOToDate(value)}`)
        },
        {
            field: "altitude_min",
            headerName: "Alt Min ft (AGL)",
            editable: false,
            minWidth: 125,
            flex: 1,
            valueGetter: (value, row) => `${ConvertMetersToFeet(row.altitude_min_agl_m)}`
        },
        {
            field: "altitude_max",
            headerName: "Alt Max ft (AGL)",
            editable: false,
            minWidth: 125,
            flex: 1,
            valueGetter: (value, row) => `${ConvertMetersToFeet(row.altitude_max_agl_m)}`
        },
        {
            field: "color_name",
            headerName: "Color",
            editable: false,
            minWidth: 75,
            flex: 1
        },
        {
            field: "organization_id",
            headerName: "Org",
            editable: false,
            minWidth: 75,
            flex: 1,
            renderCell: (cellValues) => {
                const org = getOrganizationByID(cellValues.formattedValue);
                if (org != undefined) {
                    return <div>{org.name}</div>;
                }
            }
        },
        {
            field: "flight_name",
            headerName: "Operation",
            editable: false,
            minWidth: 125,
            flex: 1
        },
        {
            field: "flight_status",
            headerName: "Op Status",
            editable: false,
            minWidth: 125,
            flex: 1,
            valueGetter: (value, row) => {
                try {
                    const operations = [...planningOperations, ...publishedOperations];
                    const operation = operations.find((op) => op.flight_uuid === row.flight_uuid);
                    if (operation !== undefined) {
                        return operation.state;
                    } else {
                        return "";
                    }
                } catch (err) {
                    return "";
                }
            }
        },
        {
            field: "manned",
            headerName: "Alerts for",
            editable: false,
            minWidth: 100,
            flex: 1
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key={1}
                    icon={<EditIcon />}
                    label="Edit"
                    id={`edit-${row.volumeId}`}
                    data-testid="edit"
                    onClick={() => handleEditVolume(row)}
                />,
                <GridActionsCellItem
                    key={2}
                    icon={<DeleteIcon />}
                    label="Delete"
                    id={`delete-${row.volumeId}`}
                    data-testid="delete"
                    onClick={() => handleDeleteVolume(row)}
                />
            ]
        }
    ];

    useEffect(() => {
        if (alertsAreSelected === false) {
            setSelectedAlertVolumes(alertVolumes);
        }
    }, [alertVolumes, selectedAlertVolumes, alertsAreSelected]);

    const handleEditVolume = (row) => {
        navigate("/alerts", { state: { editVolume: row } });
    };

    const handleDeleteVolume = async (row) => {
        if (confirm("Are you sure you want to delete this alert volume?") === false) {
            return;
        }
        const volume = { ...row };
        volume.date_deleted = new Date();
        volume.deleted = true;

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(volume)
        };
        await fetch("api/alertVolume/insertUpdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => setSnackbar({ children: "Alert Volume Successfully Deleted", severity: "success" }))
            .catch((err) => handleFailedFetch(err));
    };

    const handleCheckboxSelected = (ids) => {
        if (ids.length > 0 && showMap === false) {
            setShowMap(true);
        }
        const selected = alertVolumes.filter(({ id }) => {
            return ids.includes(id);
        });
        if (selected.length > 0) {
            setAlertsAreSelected(true);
            setSelectedAlertVolumes(selected);
        } else {
            setAlertsAreSelected(false);
            setSelectedAlertVolumes(alertVolumes);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "column" },
                width: "100%",
                height: "100%",
                justifyContent: { xs: "flex-end", sm: "flex-start" }
            }}
        >
            <Paper
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "column" },
                    gap: { xs: 1, sm: 2 },
                    p: { xs: 1, sm: 2 },
                    borderTopRightRadius: 0,
                    botderTopLeftRadius: 0,
                    height: { xs: "100%", sm: "578px" },
                    flexShrink: { xs: "unset", sm: 0 }
                }}
            >
                <Box sx={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <ResponsiveGrid
                        primaryColumns={["name", "time_start"]}
                        rows={alertVolumes}
                        columns={columns}
                        checkboxSelection
                        disableVirtualization
                        disableRowSelectionOnClick
                        pagination
                        slots={{ pagination: CustomPagination }}
                        onRowSelectionModelChange={handleCheckboxSelected}
                        sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
                    />
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        data-testid="largePreview"
                        onClick={() => setShowMap((prev) => !prev)}
                        sx={{ mr: "auto", whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                        {showMap ? "Hide Map" : "Preview on Map"}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        data-testid="create"
                        id="createAlertVolume"
                        onClick={() => navigate("/alerts")}
                        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                    >
                        Create Alert Volume
                    </Button>
                </Box>
            </Paper>

            {showMap ? (
                <Paper sx={{ flexShrink: 0, height: { xs: "40%", sm: "400px" }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 0 }, pb: { xs: 0, sm: 2 } }}>
                    <MapPreview selected_alert_volumes={selectedAlertVolumes} />
                </Paper>
            ) : (
                <></>
            )}
        </Box>
    );
}
